// src/components/ui/BasicDialog.jsx
import React from "react";
import { cn } from "../../utils/cn";

export const BASIC_DIALOG_SURFACE_CLASS =
  "relative w-[calc(100vw-32px)] sm:w-[512px] max-w-[560px] min-w-[288px] bg-[rgb(var(--clr-surface-cont-highest))] rounded-[28px] overflow-hidden shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.06),0px_6px_12px_-4px_rgba(29,79,215,0.06),0px_3px_6px_-2px_rgba(26,27,35,0.06),0px_3px_3px_-1px_rgba(26,27,35,0.06),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),0px_0px_0px_1px_rgba(24,50,119,0.10)]";

export const BASIC_DIALOG_TITLE_CLASS =
  "text-headline-small text-on-surface";

export const BASIC_DIALOG_BODY_CLASS =
  "text-body-medium text-on-surface-variant";

export const BASIC_DIALOG_ACTION_CLASS =
  "flex-1 h-12 min-h-12 rounded-xl border-0 px-6 py-3 text-label-large whitespace-nowrap transition-colors";

export const BASIC_DIALOG_SECONDARY_ACTION_CLASS =
  "bg-[var(--state-on-surface-08)] text-primary hover:bg-[rgba(86,106,139,0.12)]";

export const BASIC_DIALOG_PRIMARY_ACTION_CLASS =
  "bg-[rgb(var(--afrimed-primary))] text-[rgb(var(--afrimed-on-primary))] hover:opacity-95 disabled:bg-[var(--state-primary-12)] disabled:text-on-surface-variant";

export function BasicDialogIcon({ src = "/icons/dialog-check.svg", alt = "" }) {
  if (!src) return null;
  return <img src={src} alt={alt} className="mx-auto block h-11 w-11" />;
}

export function BasicDialogFrame({
  title,
  body,
  iconSrc = null,
  iconAlt = "",
  titleId,
  children,
  className = "",
  contentClassName = "",
  actionsClassName = "",
  as: Component = "div",
  ...props
}) {
  return (
    <Component className={cn(BASIC_DIALOG_SURFACE_CLASS, className)} {...props}>
      <div className={cn("px-6 pt-6", contentClassName)}>
        {iconSrc ? <BasicDialogIcon src={iconSrc} alt={iconAlt} /> : null}
        <div
          id={titleId}
          className={`${BASIC_DIALOG_TITLE_CLASS}${iconSrc ? " mt-4" : ""}`}
        >
          {title}
        </div>
        <div className={`${BASIC_DIALOG_BODY_CLASS} mt-4 leading-5`}>{body}</div>
      </div>

      <div className={cn("flex gap-2 px-6 pb-6 pt-6", actionsClassName)}>
        {children}
      </div>
    </Component>
  );
}

export default function BasicDialog({...props}) {
  const {
    open, title, body,
    cancelText="Cancel", okText="OK",
    onCancel, onOk,
    disableBackdropClose=false,
    actionClassName="",
    actionTextColor,
  } = props;
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* backdrop — don’t close if disabled */}
      <div
        className="absolute inset-0 bg-black/35"
        onClick={disableBackdropClose ? undefined : onCancel}
      />
      <BasicDialogFrame
        role="dialog"
        aria-modal="true"
        title={title}
        body={body}
        contentClassName="pt-7 pb-3 sm:pt-8 sm:pb-4"
        actionsClassName="pt-2 sm:pt-3"
      >
          <button
            className={cn(BASIC_DIALOG_ACTION_CLASS, BASIC_DIALOG_SECONDARY_ACTION_CLASS, actionClassName)}
            style={{
              color: actionTextColor || "rgb(var(--clr-on-surface))",
            }}
            onClick={onCancel}
          >
            {cancelText}
          </button>

          <button
            className={cn(BASIC_DIALOG_ACTION_CLASS, BASIC_DIALOG_PRIMARY_ACTION_CLASS, actionClassName)}
            style={{
              color: actionTextColor || "rgb(var(--afrimed-on-primary))",
            }}
            onClick={onOk}
          >
            {okText}
          </button>
      </BasicDialogFrame>
    </div>
  );
}
